import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  adminApi,
  ApiError,
  type AdminOverview,
  type Lead,
  type LeadStage,
  type PartnerOverview,
} from "../api";
import { Banner, CopyField, StatTile } from "../components/ui";
import { geoList, fullDate, relativeAge, shortDate } from "../format";

/**
 * The team's board: who is referring, who they sent, and where each
 * conversation has got to.
 *
 * Every stage change and note edit writes straight through to the Worker and
 * then re-reads the row it changed, rather than optimistically patching local
 * state. At this scale the round trip is imperceptible, and it means the table
 * can never show a stage that failed to save — which, on a board people use to
 * decide who to chase, is the failure that actually costs something.
 */
export default function Admin({ token, onSignOut }: { token: string; onSignOut: () => void }) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filters, setFilters] = useState<{ ref: string; stage: string }>({ ref: "", stage: "" });
  const [error, setError] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<{ slug: string; key: string } | null>(null);

  const handle = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      setError(err instanceof Error ? err.message : "Something went wrong.");
    },
    [onSignOut],
  );

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await adminApi.overview(token));
      setError(null);
    } catch (err) {
      handle(err);
    }
  }, [token, handle]);

  const loadLeads = useCallback(async () => {
    try {
      const { leads: rows } = await adminApi.leads(token, {
        ref: filters.ref || undefined,
        stage: filters.stage || undefined,
      });
      setLeads(rows);
      setError(null);
    } catch (err) {
      handle(err);
    }
  }, [token, filters, handle]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    void loadLeads();
  }, [loadLeads]);

  async function patchLead(id: string, patch: { stage?: LeadStage; notes?: string | null }) {
    try {
      const { lead } = await adminApi.updateLead(token, id, patch);
      setLeads((rows) => rows.map((row) => (row.id === id ? lead : row)));
      // A stage change moves the counts in the tiles above, so they have to
      // be refetched or they quietly go stale for the rest of the session.
      if (patch.stage) void loadOverview();
    } catch (err) {
      handle(err);
    }
  }

  async function rotate(slug: string) {
    try {
      const { accessKey } = await adminApi.rotateKey(token, slug);
      setRevealedKey({ slug, key: accessKey });
    } catch (err) {
      handle(err);
    }
  }

  async function toggleStatus(partner: PartnerOverview) {
    try {
      await adminApi.setStatus(token, partner.slug, partner.status === "active" ? "paused" : "active");
      void loadOverview();
    } catch (err) {
      handle(err);
    }
  }

  if (!overview) {
    return (
      <div className="shell">
        {error ? <Banner tone="error">{error}</Banner> : <span className="dim">Loading…</span>}
      </div>
    );
  }

  const { config, stageOrder } = overview;
  const misconfigured = [
    !config.telegram && "Telegram bot (TELEGRAM_BOT_TOKEN / TELEGRAM_CHAT_ID) — new leads are not being announced",
    !config.visitSalt && "VISIT_SALT — visitor counts stay at zero until it is set",
  ].filter(Boolean) as string[];

  return (
    <div className="shell">
      <div className="topbar">
        <div>
          <h1>Partners</h1>
          <div className="sub">
            {overview.partners.length} partner{overview.partners.length === 1 ? "" : "s"} ·{" "}
            {overview.totalLeads} lead{overview.totalLeads === 1 ? "" : "s"} all time
          </div>
        </div>
        <button className="ghost small" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      {error ? <Banner tone="error">{error}</Banner> : null}

      {misconfigured.length > 0 ? (
        <Banner tone="warn">
          <strong>Not fully configured.</strong> These are missing on the Worker:
          <ul>
            {misconfigured.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {revealedKey ? (
        <Banner tone="good">
          <strong>Access key for {revealedKey.slug}</strong> — send it now, it cannot be shown
          again. The partner opens this link and stays signed in:
          <div style={{ marginTop: 10 }}>
            {/* A fragment, not a query string — see claimKeyFromUrl. A `?key=`
                would land in Cloudflare's access logs for this origin. */}
            <CopyField value={`${window.location.origin}/#key=${revealedKey.key}`} />
          </div>
          <button className="ghost small" style={{ marginTop: 10 }} onClick={() => setRevealedKey(null)}>
            Done
          </button>
        </Banner>
      ) : null}

      <section>
        <div className="tiles">
          <StatTile label="Leads" value={overview.totalLeads} />
          {stageOrder.map((stage) => (
            <StatTile key={stage} label={stage} value={overview.stages[stage] ?? 0} />
          ))}
        </div>
      </section>

      <section>
        <h2>Referring partners</h2>
        <PartnerTable
          partners={overview.partners}
          siteOrigin={config.siteOrigin}
          onFilter={(ref) => setFilters((f) => ({ ...f, ref }))}
          onRotate={rotate}
          onToggleStatus={toggleStatus}
        />
        {overview.unregisteredRefs.length > 0 ? (
          <Banner tone="warn">
            <strong>Leads arrived on referral codes with no partner account.</strong> Add the
            partner below with the matching slug and their history attaches automatically.
            <ul>
              {overview.unregisteredRefs.map((r) => (
                <li key={r.ref}>
                  <code>{r.ref}</code> — {r.count} lead{r.count === 1 ? "" : "s"}
                </li>
              ))}
            </ul>
          </Banner>
        ) : null}
        <AddPartner
          token={token}
          onCreated={(slug, key) => {
            setRevealedKey({ slug, key });
            void loadOverview();
          }}
          onError={handle}
        />
      </section>

      <section>
        <div className="row spread" style={{ marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Pipeline</h2>
          <div className="row">
            <select
              value={filters.ref}
              onChange={(e) => setFilters((f) => ({ ...f, ref: e.target.value }))}
            >
              <option value="">All partners</option>
              {overview.partners.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.display_name}
                </option>
              ))}
            </select>
            <select
              value={filters.stage}
              onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
            >
              <option value="">All stages</option>
              {stageOrder.map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
          </div>
        </div>

        <LeadTable leads={leads} stages={stageOrder} onPatch={patchLead} />
      </section>
    </div>
  );
}

function PartnerTable({
  partners,
  siteOrigin,
  onFilter,
  onRotate,
  onToggleStatus,
}: {
  partners: PartnerOverview[];
  siteOrigin: string;
  onFilter: (ref: string) => void;
  onRotate: (slug: string) => void;
  onToggleStatus: (partner: PartnerOverview) => void;
}) {
  if (partners.length === 0) {
    return (
      <div className="table-wrap">
        <p className="empty">No partners yet. Add the first one below.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Partner</th>
            <th>Link</th>
            <th className="num">Visitors</th>
            <th className="num">Leads</th>
            <th className="num">Live</th>
            <th>Last lead</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {partners.map((partner) => (
            <tr key={partner.slug}>
              <td>
                <div>{partner.display_name}</div>
                <div className="tiny dim">
                  <code>{partner.slug}</code>
                  {partner.status !== "active" ? <span className="pill paused"> paused</span> : null}
                </div>
              </td>
              <td className="tiny dim nowrap">
                {siteOrigin.replace(/^https?:\/\//, "")}/r/{partner.slug}
              </td>
              <td className="num">{partner.visitors}</td>
              <td className="num">
                <button className="ghost small" onClick={() => onFilter(partner.slug)}>
                  {partner.leads}
                </button>
              </td>
              <td className="num">{partner.live}</td>
              <td
                className="tiny dim nowrap"
                title={fullDate(partner.last_lead_at)}
              >
                {relativeAge(partner.last_lead_at)}
              </td>
              <td className="nowrap">
                <button className="ghost small" onClick={() => onRotate(partner.slug)}>
                  New key
                </button>{" "}
                <button className="ghost small" onClick={() => onToggleStatus(partner)}>
                  {partner.status === "active" ? "Pause" : "Resume"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadTable({
  leads,
  stages,
  onPatch,
}: {
  leads: Lead[];
  stages: LeadStage[];
  onPatch: (id: string, patch: { stage?: LeadStage; notes?: string | null }) => void;
}) {
  if (leads.length === 0) {
    return (
      <div className="table-wrap">
        <p className="empty">Nothing matches this filter.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Arrived</th>
            <th>Who</th>
            <th>Contact</th>
            <th>Profile</th>
            <th>Via</th>
            <th>Stage</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id}>
              <td className="nowrap num tiny dim" title={fullDate(lead.created_at)}>
                {shortDate(lead.created_at)}
              </td>
              <td>
                <div>{lead.company || lead.name}</div>
                {lead.company ? <div className="tiny dim">{lead.name}</div> : null}
                {lead.message ? (
                  <details className="tiny dim">
                    <summary style={{ cursor: "pointer" }}>message</summary>
                    <div style={{ whiteSpace: "pre-wrap", maxWidth: 320, marginTop: 4 }}>
                      {lead.message}
                    </div>
                  </details>
                ) : null}
              </td>
              <td className="tiny">
                {lead.email ? (
                  <div>
                    <a href={`mailto:${lead.email}`}>{lead.email}</a>
                  </div>
                ) : null}
                {lead.telegram ? (
                  <div>
                    <a
                      href={`https://t.me/${lead.telegram.replace(/^@/, "")}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {lead.telegram}
                    </a>
                  </div>
                ) : null}
              </td>
              <td className="tiny dim">
                <div>{geoList(lead.geo)}</div>
                <div>
                  {lead.license || "—"} · {lead.ggr || "—"}
                </div>
                {lead.interest ? <div>{lead.interest}</div> : null}
              </td>
              <td className="tiny">
                {lead.ref ? <code>{lead.ref}</code> : <span className="dim">direct</span>}
                {lead.country ? <div className="dim">{lead.country}</div> : null}
              </td>
              <td>
                <select
                  className="stage"
                  data-stage={lead.stage}
                  value={lead.stage}
                  onChange={(e) => onPatch(lead.id, { stage: e.target.value as LeadStage })}
                  aria-label={`Stage for ${lead.company || lead.name}`}
                >
                  {stages.map((stage) => (
                    <option key={stage} value={stage}>
                      {stage}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <NoteCell
                  value={lead.notes ?? ""}
                  onSave={(notes) => onPatch(lead.id, { notes })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Inline note editing. Saves on blur rather than on every keystroke — a PATCH
 * per character would be absurd — and only when the text actually changed, so
 * tabbing through the table does not write a row back unmodified.
 */
function NoteCell({ value, onSave }: { value: string; onSave: (notes: string | null) => void }) {
  const [draft, setDraft] = useState(value);

  // Keeps the field in step when the row is replaced by a server response.
  useEffect(() => setDraft(value), [value]);

  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft.trim() !== value.trim()) onSave(draft.trim() || null);
      }}
      placeholder="—"
      style={{ minWidth: 150, fontSize: 13, padding: "5px 8px" }}
      aria-label="Internal note"
    />
  );
}

function AddPartner({
  token,
  onCreated,
  onError,
}: {
  token: string;
  onCreated: (slug: string, key: string) => void;
  onError: (err: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ slug: "", displayName: "", contactEmail: "", contactTelegram: "" });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const { accessKey } = await adminApi.createPartner(token, {
        slug: form.slug.trim(),
        displayName: form.displayName.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        contactTelegram: form.contactTelegram.trim() || undefined,
      });
      onCreated(form.slug.trim(), accessKey);
      setForm({ slug: "", displayName: "", contactEmail: "", contactTelegram: "" });
      setOpen(false);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="ghost small" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
        + Add a partner
      </button>
    );
  }

  return (
    <form className="card" style={{ marginTop: 12 }} onSubmit={submit}>
      <label className="field">
        <span>Referral code (slug)</span>
        <input
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
          placeholder="acme"
          pattern="[a-z0-9][a-z0-9-]{0,31}"
          title="Lowercase letters, digits and hyphens; cannot start with a hyphen."
          required
        />
      </label>
      <label className="field">
        <span>Display name</span>
        <input
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          placeholder="Acme Partners"
          required
        />
      </label>
      <div className="row">
        <label className="field" style={{ flex: 1, minWidth: 180 }}>
          <span>Their email (optional)</span>
          <input
            type="email"
            value={form.contactEmail}
            onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
          />
        </label>
        <label className="field" style={{ flex: 1, minWidth: 180 }}>
          <span>Their Telegram (optional)</span>
          <input
            value={form.contactTelegram}
            onChange={(e) => setForm({ ...form, contactTelegram: e.target.value })}
            placeholder="@handle"
          />
        </label>
      </div>
      <p className="tiny dim">
        The slug must match the one added to <code>src/lib/partners.ts</code> in the solex-website
        repo, which is what gives them a branded <code>/r/</code> landing page. Without that entry
        the code still works and still attributes leads — the visitor just lands on the ordinary
        contact page.
      </p>
      <div className="row">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Creating…" : "Create and show key"}
        </button>
        <button type="button" className="ghost" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
